import React, { useEffect, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { COLORS, FONTS } from '../constants/config';
import { DeletionImpact, CascadeImpactItem } from '../types';
import { Ionicons } from '@expo/vector-icons';

interface DeletionImpactModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  entityName: string;
  fetchImpact: () => Promise<DeletionImpact>;
  isOrgEmployee: boolean;
  isSubmitting?: boolean;
}

export default function DeletionImpactModal({
  visible,
  onClose,
  onConfirm,
  entityName,
  fetchImpact,
  isOrgEmployee,
  isSubmitting = false,
}: DeletionImpactModalProps) {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [impact, setImpact] = useState<DeletionImpact | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      loadImpact();
    } else {
      setImpact(null);
      setError(null);
    }
  }, [visible]);

  const loadImpact = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchImpact();
      setImpact(data);
    } catch (err) {
      setError(t('messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type: string) => {
    return t(`deletion.${type}`, type);
  };

  const renderChildren = (
    allItems: CascadeImpactItem[],
    parentType: string,
    parentId: number,
    depth: number,
  ) => {
    const children = allItems.filter(
      (c) => c.parent_type === parentType && c.parent_id === parentId,
    );
    if (children.length === 0) return null;

    return children.map((child, index) => (
      <View key={`${child.type}-${child.id}`}>
        <View style={[styles.treeItem, { [isRTL ? 'marginRight' : 'marginLeft']: depth * 20 }]}>
          <Text style={styles.treeBranch}>
            {index === children.length - 1 ? '└── ' : '├── '}
          </Text>
          <Text style={[styles.treeItemText, isRTL && styles.rtlText]}>
            {child.name} ({getTypeLabel(child.type)})
          </Text>
        </View>
        {renderChildren(allItems, child.type, child.id, depth + 1)}
      </View>
    ));
  };

  const renderTree = (items: CascadeImpactItem[]) => {
    const rootItems = items.filter((i) => i.is_root);

    return (
      <View style={styles.treeContainer}>
        {rootItems.map((root) => (
          <View key={`${root.type}-${root.id}`}>
            <View style={[styles.treeItem, styles.rootItem]}>
              <Ionicons name="trash-outline" size={16} color="#EF4444" />
              <Text style={[styles.treeItemText, styles.rootItemText, isRTL && styles.rtlText]}>
                {root.name} ({getTypeLabel(root.type)})
              </Text>
            </View>
            {renderChildren(items, root.type, root.id, 1)}
          </View>
        ))}
      </View>
    );
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <Ionicons name="warning" size={24} color="#EAB308" />
            <Text style={[styles.headerTitle, isRTL && styles.rtlText]}>
              {t('deletion.impactTitle')}
            </Text>
          </View>

          <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
            {loading ? (
              <ActivityIndicator size="large" color={COLORS.primary} />
            ) : error ? (
              <Text style={styles.errorText}>{error}</Text>
            ) : impact ? (
              <>
                <Text style={[styles.description, isRTL && styles.rtlText]}>
                  {t('deletion.impactDescription')}
                </Text>

                {renderTree(impact.items)}

                {impact.unlinked_kpis.length > 0 && (
                  <View style={styles.warningBox}>
                    <Ionicons name="link-outline" size={16} color="#EAB308" />
                    <Text style={[styles.warningText, isRTL && styles.rtlText]}>
                      {t('deletion.unlinkedKpis', { count: impact.unlinked_kpis.length })}
                    </Text>
                  </View>
                )}

                <View style={styles.summaryBox}>
                  <Text style={[styles.summaryText, isRTL && styles.rtlText]}>
                    {t('deletion.totalAffected', { count: impact.total_affected })}
                  </Text>
                </View>
              </>
            ) : null}
          </ScrollView>

          <View style={[styles.actions, isRTL && styles.actionsRTL]}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={onClose}
              disabled={isSubmitting}
            >
              <Text style={styles.cancelButtonText}>{t('common.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.deleteButton, (loading || isSubmitting) && styles.disabledButton]}
              onPress={onConfirm}
              disabled={loading || isSubmitting || !!error}
            >
              {isSubmitting ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.deleteButtonText}>
                  {isOrgEmployee ? t('deletion.submitRequest') : t('common.delete')}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    paddingBottom: 12,
    gap: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
  },
  headerTitle: {
    fontSize: 18,
    fontFamily: FONTS.semiBold,
    color: '#171717',
  },
  content: {
    maxHeight: 400,
  },
  contentContainer: {
    padding: 20,
  },
  description: {
    fontSize: 14,
    color: '#525252',
    marginBottom: 16,
    fontFamily: FONTS.regular,
  },
  treeContainer: {
    marginBottom: 16,
  },
  treeItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    gap: 6,
  },
  rootItem: {
    paddingVertical: 8,
  },
  treeItemText: {
    fontSize: 14,
    color: '#171717',
    fontFamily: FONTS.regular,
  },
  rootItemText: {
    fontFamily: FONTS.semiBold,
  },
  treeBranch: {
    fontSize: 14,
    color: '#A3A3A3',
    fontFamily: 'monospace',
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FEF9C3',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  warningText: {
    fontSize: 13,
    color: '#854D0E',
    fontFamily: FONTS.regular,
    flex: 1,
  },
  summaryBox: {
    backgroundColor: '#F5F5F5',
    padding: 12,
    borderRadius: 8,
  },
  summaryText: {
    fontSize: 14,
    fontFamily: FONTS.semiBold,
    color: '#171717',
    marginBottom: 4,
  },
  restoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  restoreText: {
    fontSize: 13,
    color: '#16A34A',
    fontFamily: FONTS.regular,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 16,
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E5E5',
  },
  actionsRTL: {
    flexDirection: 'row-reverse',
  },
  cancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D4D4D4',
  },
  cancelButtonText: {
    fontSize: 14,
    color: '#525252',
    fontFamily: FONTS.medium,
  },
  deleteButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: '#EF4444',
  },
  deleteButtonText: {
    fontSize: 14,
    color: '#fff',
    fontFamily: FONTS.medium,
  },
  disabledButton: {
    opacity: 0.5,
  },
  errorText: {
    fontSize: 14,
    color: '#EF4444',
    textAlign: 'center',
  },
  rtlText: {
    textAlign: 'right',
  },
});
